// Implements: F-IDN-014, F-UIX-007
/**
 * The six-digit code as six boxes (v3 `.otp`), one named group. Typing a digit moves to the next
 * box, Backspace on an empty box moves back, and pasting or typing several digits fills the boxes
 * from the one that has the focus. Only digits are kept.
 */

import { useRef } from "react";
import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import styles from "./TotpCodeInput.module.css";

export const CODE_LENGTH = 6;

export interface TotpCodeInputProps {
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly invalid: boolean;
  readonly describedBy?: string | undefined;
  readonly disabled?: boolean | undefined;
  /** The first box takes the focus when the step opens. */
  readonly firstRef?: React.RefObject<HTMLInputElement | null> | undefined;
}

export function TotpCodeInput({ value, onChange, invalid, describedBy, disabled = false, firstRef }: TotpCodeInputProps): ReactElement {
  const { t } = useTranslation();
  const boxes = useRef<(HTMLInputElement | null)[]>([]);
  const digits = Array.from({ length: CODE_LENGTH }, (_, index) => value.charAt(index));

  const focusBox = (index: number): void => {
    boxes.current[Math.max(0, Math.min(CODE_LENGTH - 1, index))]?.focus();
  };

  const write = (index: number, typed: string): void => {
    const incoming = typed.replace(/\D/g, "");
    const next = [...digits];
    if (incoming === "") {
      next[index] = "";
    } else {
      for (let offset = 0; offset < incoming.length && index + offset < CODE_LENGTH; offset += 1) {
        next[index + offset] = incoming.charAt(offset);
      }
    }
    onChange(next.join("").slice(0, CODE_LENGTH));
    if (incoming !== "") focusBox(index + incoming.length);
  };

  return (
    <div className={styles.otp} role="group" aria-label={t("account.totp.codeGroup")} aria-describedby={describedBy}>
      {digits.map((digit, index) => (
        <input
          // The boxes never reorder: the index is their identity.
          key={index}
          ref={(element) => {
            boxes.current[index] = element;
            if (index === 0 && firstRef !== undefined) firstRef.current = element;
          }}
          className={styles.box}
          inputMode="numeric"
          autoComplete={index === 0 ? "one-time-code" : "off"}
          aria-label={t("account.totp.digit", { n: index + 1 })}
          aria-invalid={invalid}
          disabled={disabled}
          value={digit}
          onChange={(event) => {
            write(index, event.target.value.replace(digit, "") || event.target.value);
          }}
          onKeyDown={(event) => {
            if (event.key === "Backspace" && digit === "" && index > 0) {
              event.preventDefault();
              const next = [...digits];
              next[index - 1] = "";
              onChange(next.join(""));
              focusBox(index - 1);
            }
          }}
          onPaste={(event) => {
            event.preventDefault();
            write(index, event.clipboardData.getData("text"));
          }}
        />
      ))}
    </div>
  );
}
